/**
 * ドロップダウン（汎用・data 属性駆動）
 *
 * マークアップ規約:
 *   <div class="lms-dropdown" data-lms-dropdown>
 *     <button data-lms-dropdown-trigger aria-expanded="false" aria-haspopup="true">…</button>
 *     <div class="lms-dropdown__panel" data-lms-dropdown-panel hidden>
 *       <button data-lms-dropdown-close>…</button>   ← 押すとパネルを閉じる（任意）
 *       …
 *     </div>
 *   </div>
 *
 * 挙動（AUTH-R5・design.md D1）:
 *   - トリガー押下でトグル、ESC・範囲外クリックで閉じる
 *   - 閉時は panel に hidden + inert を付与する
 *   - htmx の部分更新でヘッダー（#lms-main の外）は再描画されないため、遷移直前に
 *     closeAllDropdowns() を呼んで開いたまま残さない（main.ts の htmx:beforeSwap）
 *   - 開いたら lms:dropdown:open を発火する（モバイルドロワーとの排他。main.ts が購読して
 *     closeDrawer() を呼ぶ。polish-menu-label-calendar D2）
 *
 * フォーカス管理（レビュー Medium-4 対応。role="menu" を採用しているため WAI-ARIA APG の
 * 「開いたら最初の項目へフォーカス・閉じたらトリガーへ戻す」に沿って全経路を正規化する。
 * 矢印キーによる roving tabindex までは実装しない（この部品は最大 3 項目の単純な disclosure
 * であり、Tab キーでの逐次移動で実用上十分なため。フルの menu パターンは過剰と判断した）:
 *   - open(): パネル内の最初のフォーカス可能要素へフォーカスする
 *   - close(): ESC 経路は常にトリガーへフォーカスを戻す。それ以外（範囲外クリック・
 *     data-lms-dropdown-close）は「閉じる直前にフォーカスがパネル内にあったか」で判定する。
 *
 * 実行順に依存しない構造的な解決（レビュー再指摘 Medium-1 対応・2026-07-25）:
 *   パスワード変更ボタンのように data-lms-dropdown-close と data-lms-modal-open を同一要素に
 *   併せ持つ場合、modal.ts が同じクリックで別のフォーカス管理（openModal 内の focus）を行う。
 *   当初案は「modal.ts のリスナが先に発火してフォーカスを奪うので、この close() の
 *   wasFocusInPanel 判定が自然に false になる」という **listener 登録順（initModal を
 *   initDropdown より先に呼ぶ main.ts の配線）に依存する解決**だったが、登録順が入れ替わると
 *   静かに壊れる（トリガーへの再フォーカスがモーダル内のフォーカスを奪い返す）ため、
 *   data-lms-modal-open を兼ねる close ボタンは、そのボタン自身の DOM 属性を見て
 *   フォーカス復帰を明示的にスキップする（initDropdown 内で判定し close(entry, {
 *   skipFocusReturn: true }) を渡す）。この判定はクリックされた要素の属性を直接読むだけで、
 *   modal.ts 側のリスナが先に走ったかどうかに一切依存しない。
 */

interface DropdownEntry {
  container: HTMLElement;
  trigger: HTMLElement;
  panel: HTMLElement;
}

const FOCUSABLE_IN_PANEL = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * {@code candidates} のうち実際に見える（非表示でない）先頭要素を返す。{@code modal.ts} の
 * {@code focusables()} と可視性フィルタを揃える（レビュー Low-1 対応。汎用部品として
 * hidden/disabled/inert な要素を誤ってフォーカスしないため）。
 */
function firstVisibleFocusable(panel: HTMLElement): HTMLElement | null {
  const candidates = panel.querySelectorAll<HTMLElement>(FOCUSABLE_IN_PANEL);
  for (const el of Array.from(candidates)) {
    if (el.offsetParent !== null || el === document.activeElement) return el;
  }
  return null;
}

/** 現在開いているドロップダウンの集合 */
const openEntries = new Set<DropdownEntry>();

/**
 * ドロップダウンが開いたことを知らせるイベント名（`modal.ts` の `lms:modal:open` と同じ流儀）。
 * この部品は「開いた」事実だけを通知し、それを受けて何を閉じるかは購読側（`main.ts`）が決める。
 * ここから `drawer.ts` を import すると相互 import になるため、結線をイベントに逃がしている
 * （polish-menu-label-calendar D2）。
 */
export const DROPDOWN_OPEN_EVENT = "lms:dropdown:open";

let globalListenersBound = false;

function open(entry: DropdownEntry): void {
  entry.panel.hidden = false;
  entry.panel.removeAttribute("inert");
  entry.trigger.setAttribute("aria-expanded", "true");
  openEntries.add(entry);

  // WAI-ARIA APG「メニューを開いたら最初の項目へフォーカス」
  firstVisibleFocusable(entry.panel)?.focus();

  // 開き終わった状態（パネル可視・openEntries 登録済み・フォーカス移動済み）で通知する。
  // 購読側が状態を読んでも中間状態を見ないようにするため、必ずこの位置に置く。
  entry.container.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT, { bubbles: true }));
}

function close(
  entry: DropdownEntry,
  options: { forceFocusTrigger?: boolean; skipFocusReturn?: boolean } = {},
): void {
  // hidden/inert を付与する前に判定する（付与後は activeElement がブラウザにより
  // document.body 等へ強制的に移されるため、パネル内にあったかどうかを判定できなくなる）。
  const wasFocusInPanel = entry.panel.contains(document.activeElement);

  entry.panel.hidden = true;
  entry.panel.setAttribute("inert", "");
  entry.trigger.setAttribute("aria-expanded", "false");
  openEntries.delete(entry);

  // data-lms-modal-open を兼ねる close ボタン経由の場合、これから開くモーダルへ
  // フォーカスを譲るためここでは戻さない（実行順に依存しない構造的な回避。上記ファイル
  // 先頭コメント参照）。
  if (options.skipFocusReturn) return;

  if (options.forceFocusTrigger || wasFocusInPanel) {
    entry.trigger.focus();
  }
}

/** 開いている全ドロップダウンを閉じる（htmx 部分更新の直前に呼ぶ・design.md D1）。 */
export function closeAllDropdowns(): void {
  for (const entry of Array.from(openEntries)) {
    close(entry);
  }
}

function bindGlobalListenersOnce(): void {
  if (globalListenersBound) return;
  globalListenersBound = true;

  document.addEventListener("click", (e) => {
    if (openEntries.size === 0) return;
    const target = e.target;
    for (const entry of Array.from(openEntries)) {
      if (target instanceof Node && entry.container.contains(target)) continue;
      close(entry);
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || openEntries.size === 0) return;
    for (const entry of Array.from(openEntries)) {
      close(entry, { forceFocusTrigger: true });
    }
  });
}

/** {@code scope} 内の {@code [data-lms-dropdown]} を初期化する。多重初期化は dataset フラグでガードする。 */
export function initDropdown(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-lms-dropdown]").forEach((container) => {
    if (container.dataset["lmsDropdownReady"] === "1") return;
    container.dataset["lmsDropdownReady"] = "1";

    const trigger = container.querySelector<HTMLElement>("[data-lms-dropdown-trigger]");
    const panel = container.querySelector<HTMLElement>("[data-lms-dropdown-panel]");
    if (!trigger || !panel) return;

    panel.hidden = true;
    panel.setAttribute("inert", "");
    trigger.setAttribute("aria-expanded", "false");

    const entry: DropdownEntry = { container, trigger, panel };

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      if (openEntries.has(entry)) {
        close(entry);
      } else {
        open(entry);
      }
    });

    panel.querySelectorAll<HTMLElement>("[data-lms-dropdown-close]").forEach((btn) => {
      // 同じ要素が data-lms-modal-open も兼ねる場合（例: パスワード変更ボタン）、これから
      // 開くモーダルへフォーカスを譲るため、ここでのトリガー再フォーカスはスキップする
      // （modal.ts のリスナ登録順に依存しない。レビュー再指摘 Medium-1 対応）。
      const opensModal = btn.hasAttribute("data-lms-modal-open");
      btn.addEventListener("click", () => {
        close(entry, { skipFocusReturn: opensModal });
      });
    });
  });

  bindGlobalListenersOnce();
}
